import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function AdminSurveys() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("submitted");
    const [preview, setPreview] = useState(null);

    const load = () => api.get("/admin/surveys").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const filtered = filter === "all" ? items : items.filter((s) => s.status === filter);

    const approve = async (sid) => {
        await api.post(`/admin/surveys/${sid}/approve`);
        toast.success("Survey approved & live");
        setPreview(null);
        load();
    };
    const reject = async (sid) => {
        const reason = prompt("Feedback for advertiser?") || "";
        await api.post(`/admin/surveys/${sid}/reject`, { approve: false, feedback: reason });
        toast.success("Survey rejected");
        setPreview(null);
        load();
    };
    const pause = async (sid) => { await api.post(`/admin/surveys/${sid}/pause`); load(); };
    const resume = async (sid) => { await api.post(`/admin/surveys/${sid}/resume`); load(); };

    const [extendOpen, setExtendOpen] = useState(false);
    const [respDelta, setRespDelta] = useState(0);
    const [quotaDeltas, setQuotaDeltas] = useState({});

    useEffect(() => { setExtendOpen(false); setRespDelta(0); setQuotaDeltas({}); }, [preview?.id]);

    const submitExtend = async () => {
        const quota_extensions = Object.entries(quotaDeltas)
            .map(([quota_id, additional]) => ({ quota_id, additional: Number(additional) || 0 }))
            .filter((x) => x.additional > 0);
        if (!respDelta && quota_extensions.length === 0) {
            toast.error("Enter at least one extension");
            return;
        }
        try {
            const { data } = await api.post(`/admin/surveys/${preview.id}/extend`, {
                responses_needed_delta: Number(respDelta) || 0,
                quota_extensions,
            });
            toast.success(`Extended (cost delta: $${data.cost_delta_usd.toFixed(2)})`);
            setPreview(data.survey);
            setExtendOpen(false);
            setRespDelta(0);
            setQuotaDeltas({});
            load();
        } catch (_e) {
            toast.error("Extend failed");
        }
    };

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 py-12">
                <p className="eyebrow mb-2">ADMIN · SURVEYS</p>
                <h1 className="h2 mb-8">Survey approvals & oversight</h1>
                <div className="flex flex-wrap gap-2 mb-6">
                    {["submitted", "live", "draft", "rejected", "completed", "all"].map((f) => (
                        <button key={f} onClick={() => setFilter(f)} className={`btn ${filter === f ? "btn-ink" : "btn-outline"} text-sm py-2 px-3`} data-testid={`filter-${f}`}>
                            {f.toUpperCase()}
                        </button>
                    ))}
                </div>

                <div className="grid lg:grid-cols-2 gap-4">
                    <div className="card-base">
                        {filtered.length === 0 && <div className="p-8 text-center text-sm text-ink-600">None.</div>}
                        {filtered.map((s) => (
                            <button key={s.id} onClick={() => setPreview(s)} className={`w-full text-left p-5 border-b border-ink-200 hover:bg-cream ${preview?.id === s.id ? "bg-cream" : ""}`} data-testid={`adm-survey-row-${s.id}`}>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="font-heading font-bold truncate">{s.title}</div>
                                    <span className="font-mono text-[10px] text-ink-600">{s.status.toUpperCase()}</span>
                                </div>
                                <div className="text-xs text-ink-600 mt-1">
                                    {s.advertiser_company || s.advertiser_name} · {s.questions.length} q · ${s.advertiser_cost_usd}
                                </div>
                            </button>
                        ))}
                    </div>

                    <div>
                        {preview ? (
                            <div className="card-base p-6 sticky top-20">
                                <p className="eyebrow mb-2">{preview.category} · {preview.status.toUpperCase()}</p>
                                <h2 className="h3 mb-2">{preview.title}</h2>
                                <p className="text-sm text-ink-600 mb-4">{preview.description}</p>
                                <div className="grid grid-cols-2 gap-2 text-xs mb-4 border-t border-b border-ink-200 py-3">
                                    <div><span className="text-ink-600">Reward:</span> <span className="font-mono">{preview.points_per_response} pts</span></div>
                                    <div><span className="text-ink-600">Quota:</span> <span className="font-mono">{preview.responses_needed}</span></div>
                                    <div><span className="text-ink-600">Cost:</span> <span className="font-mono">${preview.advertiser_cost_usd}</span></div>
                                    <div><span className="text-ink-600">Targeted:</span> <span className="font-mono">{preview.targeted ? "Yes" : "No"}</span></div>
                                </div>
                                <div className="space-y-2 max-h-80 overflow-auto mb-4">
                                    {preview.questions.map((q, i) => (
                                        <div key={q.id} className="text-sm border border-ink-200 p-3">
                                            <div className="font-mono text-[10px] text-ink-600">Q{i + 1} · {q.type}{q.is_attention_check ? " · ATTENTION" : ""}</div>
                                            <div>{q.text}</div>
                                            {q.options?.length > 0 && (
                                                <div className="text-xs text-ink-600 mt-1">{q.options.join(" · ")}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {preview.status === "submitted" && (
                                        <>
                                            <button onClick={() => approve(preview.id)} className="btn btn-ink text-sm" data-testid="adm-approve-btn">Approve & make live</button>
                                            <button onClick={() => reject(preview.id)} className="btn btn-outline text-sm" data-testid="adm-reject-btn">Reject</button>
                                        </>
                                    )}
                                    {preview.status === "live" && (
                                        <button onClick={() => pause(preview.id)} className="btn btn-outline text-sm">Pause</button>
                                    )}
                                    {preview.status === "paused" && (
                                        <button onClick={() => resume(preview.id)} className="btn btn-ink text-sm">Resume</button>
                                    )}
                                    {["live", "paused", "completed"].includes(preview.status) && (
                                        <button onClick={() => setExtendOpen((v) => !v)} className="btn btn-outline text-sm" data-testid="adm-extend-toggle-btn">
                                            {extendOpen ? "Cancel extend" : "Extend quotas"}
                                        </button>
                                    )}
                                    <Link to={`/advertiser/surveys/${preview.id}`} className="btn btn-outline text-sm">View analytics</Link>
                                </div>
                                {extendOpen && (
                                    <div className="mt-4 border-t border-ink-200 pt-4 space-y-3" data-testid="adm-extend-panel">
                                        <p className="eyebrow">EXTEND THIS STUDY</p>
                                        <div>
                                            <label className="text-xs block mb-1">Add to overall responses_needed (current {preview.responses_needed}, completed {preview.completed_count})</label>
                                            <input type="number" min={0} value={respDelta} onChange={(e) => setRespDelta(e.target.value)} className="input-base text-sm" data-testid="adm-extend-resp-delta" />
                                        </div>
                                        {(preview.quotas || []).length > 0 && (
                                            <div className="space-y-2">
                                                <div className="text-xs eyebrow">PER-BUCKET</div>
                                                {(preview.quotas || []).map((q) => (
                                                    <div key={q.id} className="flex items-center justify-between gap-3" data-testid={`adm-extend-q-${q.id}`}>
                                                        <span className="text-sm flex-1 truncate">{q.name || q.id} <span className="font-mono text-[10px] text-ink-600">{q.current_count || 0}/{q.max_responses}</span></span>
                                                        <input
                                                            type="number" min={0} placeholder="+ add"
                                                            className="input-base text-sm w-28"
                                                            value={quotaDeltas[q.id] || ""}
                                                            onChange={(e) => setQuotaDeltas({ ...quotaDeltas, [q.id]: e.target.value })}
                                                            data-testid={`adm-extend-q-${q.id}-input`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <button onClick={submitExtend} className="btn btn-ink text-sm w-full" data-testid="adm-extend-submit-btn">
                                            Apply extension
                                        </button>
                                        <p className="text-[10px] text-ink-600 font-mono">Cost delta is logged. Advertiser will be notified. Completed → live if room.</p>
                                    </div>
                                )}
                                {preview.admin_feedback && (
                                    <div className="mt-4 text-xs text-ink-600 border-t border-ink-200 pt-3">
                                        Last feedback: {preview.admin_feedback}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="card-base p-8 text-center text-sm text-ink-600">Select a survey on the left to review.</div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
